import {
    DefaultButton, IPanelProps, IRenderFunction,
    Panel,
    PanelType, PivotItem, PrimaryButton
} from "@fluentui/react";
import { RootState } from "app/store";
import ControlMask from "components/controls/ControlMask";
import SectionItem, { ESectionItemVariant } from "components/controls/SectionItem/Index";
import TextFieldControl from "components/controls/TextFieldControl";
import ToggleControl from "components/controls/ToggleControl";
import { EViewType, fadeIn, friendlyValueToFlexibleFieldValue, panelButtonsStyles } from "global/Application";
import { TChangeTracker } from "global/EmitEvent/Delta";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import { ParentVisibilityGraphProcessor } from "global/Visibility/ParentVisibilityGraph";
import { bcbs_config_masterattribute } from "model/AttributeOptionSets.type";
import {
    IFlexibleFieldSet as FAM_IFlexibleFieldSet,
    TValue as FAM_TValue
} from 'model/FlexibleAttributeModel';
import React, { useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import FlexibleFieldView, { EScopeType } from "./FlexibleFieldView";

interface IProps extends TComponentConnectedProps {
    isOpen: boolean;
    onDismiss: any;
    category: any;
    defaultValue: FAM_IFlexibleFieldSet | null;
    onChange?: (value: FAM_IFlexibleFieldSet, changes: TChangeTracker) => void;
    visibility?: IVisibilityScope;
    disabled?: boolean;
}


const AttributeSetGridViewEditor: React.FC<IProps> = (props) => {
    const [flexibleFieldSet, setFlexibleFieldSet] = useState<FAM_IFlexibleFieldSet | null>(null);

    // allows the component to perform a local visibility management, to help with the animation and ui smoothness
    // by default this should always be visible unless components has signaled a context job termination and wants to self close
    const [selfVisible, setSelfVisible] = useState(true);

    const [changeTracker, setChangeTracker] = useState<TChangeTracker>({});

    const parentVisibilityProcessor = useMemo(() => {
        const gProcessor = new ParentVisibilityGraphProcessor(
            props.state.data.config,
            props.state.data.config.cache.parentVisibilityGraph
        );

        return gProcessor;
    }, []);


    useEffect(() => {

        if (props.isOpen) {
            setSelfVisible(true);

            setChangeTracker({});

            if (props.defaultValue)
                setFlexibleFieldSet(t => {
                    const value = structuredClone(props.defaultValue);

                    if (value?.id === 'bulk') {
                        //@ts-ignore
                        delete value?.deactivated;
                    }

                    return value;
                });
        }

    }, [props.isOpen, props.defaultValue]);


    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {

        // const configAttributes = props.state.data.config.cache.map_bcbs_config_attribute;
        // const masterAttributes = props.state?.data.config.cache.map_bcbs_config_masterattribute;

        return <FlexibleFieldView
            disabled={props.state.status.viewType !== EViewType.Modify || props.disabled}
            fields={flexibleFieldSet?.fields!}
            category={props.category}
            lastPivotItem={[onRenderProperties]}
            tiers={[]}
            bulk={flexibleFieldSet?.id === 'bulk'}
            cancellation={false}
            scope={flexibleFieldSet}
            scopeType={EScopeType.flexibleFieldSet}
            visibility={props.visibility}
            onChange={(changeEvent) => {

                if (changeEvent.field) {
                    // TODO used in emit event, but it really should change approach to compare and generate change tracker performing a delta before & after
                    setChangeTracker(t => {
                        const c = { ...t };
                        c[changeEvent.field?.configRef.id!] = {
                            field: changeEvent.field!,
                            fieldValue: changeEvent.fieldValue!,
                            value: changeEvent.value!,
                        }
                        return c;
                    });

                    setFlexibleFieldSet(xFieldSet => {
                        const nFieldSet: FAM_IFlexibleFieldSet = { ...xFieldSet! };

                        const fields = nFieldSet?.fields;
                        const field = nFieldSet?.fields?.find(q => q.id === changeEvent?.field?.id);

                        if (field) {

                            if (field && field?.values?.[0])
                                field.values[0].value = changeEvent.value!;

                            const graphChanges = parentVisibilityProcessor.processVisibilityOnAttributeChange(
                                props.state.data.config,
                                field,
                                fields
                            );

                            // TODO refactor this to a helper function so you can call the second iteration with a TTL to avoid infinite loops 
                            // and also tap into emit events for the change tracking
                            graphChanges.forEach(eGraphChange => {
                                if (fields)
                                    if (eGraphChange.newVisibility === 0) {

                                        delete nFieldSet.fields[eGraphChange.index].visibility;

                                        // TODO refactor, unify all three and clean up
                                        // * Duplicated code, this has been duplicated in addUpdateAttributeSet, AttributeSetGridViewEditor.onChange, NonStandardGridViewEditor.onChange
                                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/319161
                                        fields[eGraphChange.index].values.forEach(eachValue => {

                                            if (eGraphChange.attribute.bcbs_defaultvalue)
                                                switch (eGraphChange.masterAttribute.bcbs_attributetypecode) {

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Message(808900014)'].value:
                                                        break;

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Lookup(808900006)'].value:
                                                        throw new Error("Not implemented exception!")

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Single Line of Text(808900008)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Boolean(808900000)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Option Set(808900007)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Currency(808900002)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Date(808900003)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Whole Number(808900010)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Decimal(808900004)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Costshare(808900001)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Pharmacy Costshare(808900013)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Vision Costshare(808900012)'].value:
                                                        // blank string or no-value at all
                                                        if (
                                                            !eachValue.value ||
                                                            (eachValue.value.type === 'string' && eachValue.value.value === '')
                                                        ) {

                                                            const field = fields![eGraphChange.index];
                                                            const fieldValue = eachValue;

                                                            const value: FAM_TValue = friendlyValueToFlexibleFieldValue(
                                                                props.state.data.config,
                                                                eGraphChange.attribute.bcbs_defaultvalue!,
                                                                eGraphChange.attribute,
                                                                eGraphChange.masterAttribute,
                                                                null
                                                            );

                                                            setChangeTracker(xChange => {
                                                                const nChange: typeof xChange = { ...xChange };
                                                                nChange[field.configRef.id] = {
                                                                    field: field,
                                                                    fieldValue: structuredClone(fieldValue),
                                                                    value: value!,
                                                                }
                                                                return nChange;
                                                            });

                                                            fieldValue.value = value

                                                        }

                                                        break;

                                                    default:
                                                        break;
                                                }

                                        })
                                    }
                                    else {

                                        nFieldSet.fields[eGraphChange.index].visibility = eGraphChange.newVisibility
                                    }
                            });
                        }

                        return nFieldSet;
                    });

                }
            }}
        />
    }

    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        const isBulk = flexibleFieldSet?.id === 'bulk';

        return <PivotItem headerText={"Properties"} key={"attributeSetProperties"}>

            <SectionItem
                title="General"
                variant={ESectionItemVariant.Drawer}
            >
                <TextFieldControl
                    label={"Name"}
                    disabled={isBulk || props.disabled}
                    fieldValueOnChange={(event, value) => {
                        setFlexibleFieldSet(t => { return { ...t!, name: value.value ?? '' } })
                    }}
                    defaultFieldValue={{ type: 'string', value: flexibleFieldSet?.name ?? '' }}
                />

                <ControlMask
                    label={"Deactivate"}
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setFlexibleFieldSet(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"Deactivate"}
                        checked={flexibleFieldSet?.deactivated ?? false}
                        disabled={props.disabled}
                        onChange={(_, checked) => {
                            setFlexibleFieldSet(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />
                </ControlMask>

                {/* <Dropdown
                    label={"Status"}
                    options={statusOptions}
                    selectedKey={groupNumber?.status?.value}
                    onChange={(_, option?: IDropdownOption<FAM.TStatusOptions>) => {
                        setGroupNumber(oldState => {
                            return { ...oldState!, status: option?.data! };
                        });
                    }}
                /> */}

            </SectionItem>
        </PivotItem>
    }

    const onChange = (ffs: FAM_IFlexibleFieldSet) => {
        // const u = changeTracker;
        //         debugger;
        props.onChange?.(ffs, changeTracker);
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={props.disabled}
                onClick={async () => {
                    setSelfVisible(false);

                    setTimeout(() => {
                        if (flexibleFieldSet)
                            onChange(flexibleFieldSet);
                    }, 300);
                }}
            />
            <DefaultButton text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => {
                    props?.onDismiss();
                }}
            />
        </div>;
    }

    return (
        <Panel
            isOpen={props.isOpen && selfVisible}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            isLightDismiss
            headerText="Attribute Set"
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AttributeSetGridViewEditor)
